/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { AutoCustomiserStrategy } from '@theme/product-customiser-auto';
import { MotoCustomiserStrategy } from '@theme/product-customiser-moto';
import { onAnimationEnd } from '@theme/utilities';

/**
 * FEATURE: Product Customiser Web Component
 *
 * Integrates the external Precismo suit customiser iframe with the Shopify
 * product page. The element owns the modal `<dialog>`, the configuration-code
 * input and the postMessage bridge; the type-specific logic (auto vs moto) is
 * delegated to a strategy module.
 *
 * Config is read from a `<script type="application/json">` child to avoid HTML
 * attribute escaping issues with product titles / translated strings.
 */
if (!customElements.get('product-customiser')) {
  class ProductCustomiser extends HTMLElement {
    constructor() {
      super();

      // State
      this.initalized = false;
      this.iframeOpen = false;
      this.configurationSaving = false;
      this.iframe = null;
      this.measurementConfigId = null;

      // Data
      const configEl = this.querySelector('script[type="application/json"][data-customiser-config]');
      this.configData = JSON.parse(configEl?.textContent || '{}');
      this.type = this.configData.type || 'auto';

      // Selectors
      this.button = this.querySelector('[data-customiser-button]') || this.querySelector('button');
      this.configCodeInput = document.getElementById('configCode');
      this.dialog = this.querySelector('[data-customiser-dialog]');

      // Strategy
      this.strategy = this.type === 'moto' ? new MotoCustomiserStrategy(this) : new AutoCustomiserStrategy(this);

      // Get the config and customiser url
      this.originalConfigCode = this.getConfigCodeFromUrl();
      this.rebuildUrl();

      // Get the edit flag
      this.edit = this.getEditFlagFromUrl();

      // Get the measure-before-pay flag. True when the customer returns via the
      // "finish your order" link (?measured=1) OR when we've previously recorded
      // this config code as measured (localStorage) — so the "scan saved" state
      // also shows on cart "Edit Design", bookmarks and refreshes (same browser).
      this.measuredViaLink = this.getMeasuredFlagFromUrl();
      this.measured = this.measuredViaLink || this.isMeasuredRemembered(this.originalConfigCode);
      if (this.measuredViaLink) {
        this.rememberMeasured(this.originalConfigCode);
      }

      // Listen to window messages from the customiser
      window.addEventListener('message', this.handleWindowMessage);

      // Event listeners
      this.button?.addEventListener('click', (event) => {
        event.preventDefault();
        this.openCustomiser();
      });
      this.configCodeInput?.addEventListener('change', (event) => {
        event.preventDefault();
        this.handleConfigCodeChanges();
      });
      this.dialog?.addEventListener('close', this.handleDialogClose);
      this.querySelectorAll('[data-customiser-close]').forEach((closeButton) => {
        closeButton.addEventListener('click', () => this.closeCustomiser());
      });

      // Wire the share dialog's close controls (see method docs).
      this.setupShareForm();

      // Wire the measurement-request dialog (measure-before-pay, moto only).
      this.setupMeasurementForm();

      // If the edit flag is true, then open the customiser directly
      if (this.edit) {
        // Make sure the customiser is fully loaded before opening it
        setTimeout(() => {
          this.openCustomiser();
        }, 1000);
      }
    }

    /**
     * FEATURE: Wire up the share dialog's close controls.
     *
     * The share popup (`[data-page-customiser-form]`) is a native <dialog>
     * shown via `showModal()` (see the share methods in the strategy base).
     * `showModal()` promotes it to the browser top layer, so it renders above
     * the customiser — which is itself a `showModal()` <dialog> — without
     * needing a high z-index or being moved out of the block. The top layer
     * also ignores ancestor stacking contexts / containing blocks, so the
     * sticky `_product-details` column on the PDP can't trap it.
     */
    setupShareForm() {
      const form = this.parentElement?.querySelector('[data-page-customiser-form]');
      if (!(form instanceof HTMLDialogElement)) return;

      this.shareForm = form;
      this.wireDialogClose(form);
    }

    /**
     * Wires close button, backdrop click, and ESC to an animated close, and
     * restores background scroll on any close. Shared by setupShareForm()
     * and setupMeasurementForm().
     * @param {HTMLDialogElement} form
     */
    wireDialogClose(form) {
      form.addEventListener('close', () => document.body.classList.remove('no-scroll'));
      form.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.animatedCloseDialog(form);
      });
      form.addEventListener('click', (event) => {
        if (event.target === form) this.animatedCloseDialog(form);
      });
      form.querySelectorAll('[data-close-form]').forEach((control) => {
        control.addEventListener('click', () => this.animatedCloseDialog(form));
      });
    }

    /**
     * Replicates country-recommend-modal.js's animated-close sequence
     * manually rather than extending DialogComponent, since these dialogs
     * must stay plain top-layer <dialog>s while the main customiser dialog
     * is already open.
     * @param {HTMLDialogElement} dialog
     */
    animatedCloseDialog(dialog) {
      if (!dialog.open || dialog.classList.contains('dialog-closing')) return;

      dialog.classList.add('dialog-closing');
      onAnimationEnd(dialog).then(() => {
        dialog.classList.remove('dialog-closing');
        dialog.close(); // fires the dialog's native `close` event -> existing no-scroll cleanup
      });
    }

    /**
     * FEATURE: Wire the measurement-request dialog (measure-before-pay, moto).
     *
     * Same top-layer <dialog> pattern as setupShareForm(). On submit we capture
     * the email and fire the Flow Plus webhook (strategy.sendMeasurementRequest)
     * with the saved config-id as the reference, then let the form close.
     */
    setupMeasurementForm() {
      const form = this.parentElement?.querySelector('[data-measurement-form]');
      if (!(form instanceof HTMLDialogElement)) return;

      this.measurementForm = form;
      this.wireDialogClose(form);

      const cfForm = form.querySelector('form[data-cf-form]');
      cfForm?.addEventListener('submit', () => {
        const emailInput = cfForm.querySelector('input[type="email"], [name="email"]');
        const email = emailInput?.value || '';
        this.strategy.sendMeasurementRequest({ reference: this.measurementConfigId, email });
      });
    }

    /**
     * FEATURE: Get the measure-before-pay flag from the URL. Set on the "finish
     * your order" link we email after a scan completes (`?measured=1`); tells the
     * moto strategy to show the "measurements saved" state in the configurator.
     * @returns {boolean}
     */
    getMeasuredFlagFromUrl() {
      const urlParams = new URLSearchParams(location.search);
      return urlParams.get('measured') === '1';
    }

    /**
     * FEATURE: Recall whether a config code already has a saved scan (moto).
     *
     * Uses `measured_config_codes` in localStorage: lets the "scan saved" state show on
     * any same-browser load (cart "Edit Design", bookmark, refresh), not only via
     * the `?measured=1` email link. Cross-device coverage would need Precismo's
     * `readConfigMeta` instead.
     * @param {string} code
     * @returns {boolean}
     */
    isMeasuredRemembered(code) {
      if (this.type !== 'moto' || !code) {
        return false;
      }
      try {
        const codes = JSON.parse(localStorage.getItem('measured_config_codes') || '[]');
        return Array.isArray(codes) && codes.includes(code);
      } catch {
        return false;
      }
    }

    /** FEATURE: Record a config code as measured (see isMeasuredRemembered). */
    rememberMeasured(code) {
      if (this.type !== 'moto' || !code) {
        return;
      }
      try {
        const codes = JSON.parse(localStorage.getItem('measured_config_codes') || '[]');
        if (Array.isArray(codes) && !codes.includes(code)) {
          codes.push(code);
          localStorage.setItem('measured_config_codes', JSON.stringify(codes));
        }
      } catch {
        // localStorage unavailable — we just won't remember across loads.
      }
    }

    /**
     * FEATURE: Whether a config code has a completed SizeYou scan (moto MTM gate).
     * @param {string} code
     * @returns {boolean}
     */
    hasSavedMeasurement(code) {
      return this.type === 'moto' && Boolean(code) && this.isMeasuredRemembered(code);
    }

    /** Recompute the iframe url from the current config code. */
    rebuildUrl() {
      this.url =
        `${this.configData.url}?wait_for_initialization=true` +
        (this.originalConfigCode ? `&configurationCode=${this.originalConfigCode}` : '');
    }

    /**
     * FEATURE: Listen to post messages from the customiser and route them to
     * the active strategy.
     */
    handleWindowMessage = (e) => {
      try {
        if (!e.origin.includes('precismo') && !e.origin.includes('vercel.app')) {
          return false;
        }

        // guard against different messages
        if (typeof e.data !== 'string' || e.data.indexOf('{') == -1) {
          return false;
        }

        const data = JSON.parse(e.data);
        const type = data.type.toLowerCase();
        this.strategy.handleMessage(type, data);
      } catch (error) {
        console.error(error);
      }
    };

    /**
     * FEATURE: Open Customiser Popup (native <dialog>)
     */
    openCustomiser() {
      if (!this.url) {
        console.error('No customiser url found');
        return;
      }
      if (!this.dialog) {
        console.error('No customiser dialog found');
        return;
      }

      // Lazily create the iframe so it only loads when the modal is opened
      let iframe = this.dialog.querySelector('iframe[data-customiser-iframe]');
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.setAttribute('data-customiser-iframe', '');
        iframe.setAttribute('title', 'Configurator');
        iframe.setAttribute('allow', 'fullscreen; camera; microphone; xr-spatial-tracking');
        const frame = this.dialog.querySelector('[data-customiser-frame]') || this.dialog;
        frame.appendChild(iframe);
      }
      iframe.src = this.url;
      this.iframe = iframe;
      this.iframeOpen = true;
      document.body.classList.add('no-scroll');

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      // The auto customiser is initialized once the iframe has loaded; the moto
      // customiser initializes in response to its `readyInitialization` message.
      if (!this.initalized && this.type === 'auto') {
        iframe.addEventListener('load', () => this.strategy.handleInitialization(), { once: true });
      }
    }

    /**
     * FEATURE: Close Customiser Popup
     */
    closeCustomiser() {
      if (this.dialog?.open) {
        this.dialog.close(); // fires the `close` event -> cleanup
      } else {
        this.cleanupModal();
      }
    }

    handleDialogClose = () => this.cleanupModal();

    cleanupModal() {
      this.initalized = false;
      this.iframeOpen = false;
      if (this.iframe) {
        this.iframe.src = 'about:blank';
        this.iframe.remove();
        this.iframe = null;
      }
      document.body.classList.remove('no-scroll');
    }

    /**
     * FEATURE: Get the config code from the URL or the configuration-code input.
     *
     * We intentionally do not read `config_code` from localStorage — Precismo only
     * persists configurations the user deliberately saves or adds to cart, so
     * reloading an unsaved code causes load failures (see REMAINING-WORK.md).
     * @returns {string}
     */
    getConfigCodeFromUrl() {
      const urlParams = new URLSearchParams(location.search);
      const configCode = urlParams.get('configurationCode') || '';
      if (configCode) {
        if (this.configCodeInput) {
          this.configCodeInput.value = configCode;
        }
        return configCode;
      }
      return this.configCodeInput?.value.trim() || '';
    }

    /**
     * FEATURE: Get the edit flag from the URL. When set the customiser opens
     * directly and replaces an existing cart item with the new configuration.
     * @returns {boolean}
     */
    getEditFlagFromUrl() {
      const urlParams = new URLSearchParams(location.search);
      const edit = urlParams.get('edit') === 'true';
      if (edit) {
        // Remove 'edit' from the URL to prevent conflicts with page refresh
        const url = new URL(window.location.href);
        url.searchParams.delete('edit');
        window.history.replaceState({}, document.title, url.toString());
      }
      return edit;
    }

    /**
     * FEATURE: Listen to config code changes and update the url + config code.
     */
    handleConfigCodeChanges() {
      if (!this.configCodeInput) {
        return;
      }
      this.originalConfigCode = this.configCodeInput.value;
      this.rebuildUrl();
    }
  }

  window.ProductCustomiser = ProductCustomiser;

  customElements.define('product-customiser', ProductCustomiser);
}
